import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import StashitLogo from '../components/Logo/StashitLogo';

interface SupportCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface SupportTicket {
  id: string;
  ticket_number: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  created_at: string;
  support_categories: {
    name: string;
    icon: string;
    color: string;
  };
  locations?: {
    name: string;
    address: string;
  };
  lockers?: {
    locker_number: string;
  };
  smart_stall_compartments?: {
    compartment_number: string;
  };
  assigned_admin?: {
    full_name: string;
  };
}

interface SupportMessage {
  id: string;
  message: string;
  message_type: string;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
  };
}

const SupportPage: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('create');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  const [ticketForm, setTicketForm] = useState({
    category_id: '',
    title: '',
    description: '',
    priority: 'medium',
    location_id: '',
    locker_id: '',
    stall_id: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchTickets();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SUPPORT.CATEGORIES);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(API_ENDPOINTS.SUPPORT.TICKETS, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      console.error('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.SUPPORT.BASE}/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setMessages(data.messages || []);
        setActiveTab('tickets');
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to load ticket details');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      alert('Error loading ticket details');
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketForm.category_id || !ticketForm.title || !ticketForm.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to create a support ticket');
        return;
      }

      const response = await fetch(API_ENDPOINTS.SUPPORT.TICKETS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ticketForm)
      });

      if (response.ok) {
        const data = await response.json();
        alert('Support ticket created successfully!');
        setShowCreateForm(false);
        setTicketForm({
          category_id: '',
          title: '',
          description: '',
          priority: 'medium',
          location_id: '',
          locker_id: '',
          stall_id: ''
        });
        fetchTickets();
        // Open the created ticket
        fetchTicketDetails(data.ticket.id);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create support ticket');
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      alert('Error creating support ticket');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.SUPPORT.BASE}/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.newMessage]);
        setNewMessage('');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'pending_user': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-primary-600">Loading support...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <StashitLogo className="h-8 w-auto" />
              <h1 className="ml-4 text-2xl font-bold text-primary-700">Support Center</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'create', name: 'Create Ticket', icon: '➕' },
              { id: 'tickets', name: 'My Tickets', icon: '🎫' },
              { id: 'help', name: 'Help Center', icon: '❓' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'create' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Support Ticket</h2>
              <p className="text-gray-600 mb-6">
                Need help? Report an issue, ask a question, or submit a complaint. Our support team will respond promptly.
              </p>

              {!showCreateForm ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🎫</div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Get Help</h3>
                  <p className="text-gray-600 mb-6">
                    Choose a category and describe your issue to create a support ticket.
                  </p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Create Support Ticket
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCreateTicket} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={ticketForm.category_id}
                        onChange={(e) => setTicketForm({ ...ticketForm, category_id: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.icon} {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={ticketForm.priority}
                        onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={ticketForm.title}
                      onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={ticketForm.description}
                      onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={6}
                      placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
                      required
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                    >
                      Create Ticket
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Support Categories */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Categories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {categories.map((category) => (
                  <div key={category.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="text-2xl mb-2">{category.icon}</div>
                    <h4 className="font-medium text-gray-900 mb-1">{category.name}</h4>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">My Support Tickets</h2>
              </div>
              <div className="p-6">
                {tickets.length > 0 ? (
                  <div className="space-y-4">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => fetchTicketDetails(ticket.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-lg">{ticket.support_categories.icon}</span>
                              <h3 className="font-medium text-gray-900">{ticket.title}</h3>
                              <span className="text-sm text-gray-500">#{ticket.ticket_number}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Created: {formatDate(ticket.created_at)}</span>
                              {ticket.locations && (
                                <span>📍 {ticket.locations.name}</span>
                              )}
                              {ticket.lockers && (
                                <span>🔒 Locker {ticket.lockers.locker_number}</span>
                              )}
                              {ticket.smart_stall_compartments && (
                                <span>🏪 Stall {ticket.smart_stall_compartments.compartment_number}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex space-x-2 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                {ticket.status.replace('_', ' ')}
                              </span>
                            </div>
                            {ticket.assigned_admin && (
                              <p className="text-xs text-gray-500">
                                Assigned to: {ticket.assigned_admin.full_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎫</div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">No Support Tickets</h3>
                    <p className="text-gray-600 mb-4">You haven't created any support tickets yet.</p>
                    <button
                      onClick={() => setActiveTab('create')}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Create Your First Ticket
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Details */}
            {selectedTicket && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedTicket.title}</h3>
                      <p className="text-sm text-gray-600">#{selectedTicket.ticket_number}</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Messages */}
                  <div className="space-y-4 mb-6">
                    {messages.map((message) => (
                      <div key={message.id} className={`flex ${message.profiles.role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.profiles.role === 'admin'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <div className="text-xs opacity-75 mb-1">
                            {message.profiles.full_name} • {formatDate(message.created_at)}
                          </div>
                          <p className="text-sm">{message.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  {selectedTicket.status !== 'closed' && (
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Send
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'help' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Help Center</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">How do I book a locker?</h4>
                      <p className="text-sm text-gray-600">
                        Go to the booking page, select your location, choose a locker size, and complete the payment.
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">What payment methods are accepted?</h4>
                      <p className="text-sm text-gray-600">
                        We accept Orange Money, MyZaka, FNB, and cash payments.
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">How do I access my locker?</h4>
                      <p className="text-sm text-gray-600">
                        Use the PIN code or QR code provided in your booking confirmation.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">📞 Phone Support</h4>
                      <p className="text-sm text-blue-800">+267 XXX XXXX</p>
                      <p className="text-xs text-blue-700">Mon-Fri: 8AM-6PM, Sat: 9AM-4PM</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">📧 Email Support</h4>
                      <p className="text-sm text-green-800">support@stashit.co.bw</p>
                      <p className="text-xs text-green-700">Response within 24 hours</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-900 mb-2">💬 Live Chat</h4>
                      <p className="text-sm text-purple-800">Available in app</p>
                      <p className="text-xs text-purple-700">Mon-Fri: 8AM-6PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Support</h3>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🚨</span>
                  <div>
                    <h4 className="font-medium text-red-900">Emergency Issues</h4>
                    <p className="text-sm text-red-800 mb-2">
                      For urgent issues like locker malfunctions or security concerns, call our emergency line.
                    </p>
                    <p className="text-sm font-medium text-red-900">Emergency: +267 XXX XXXX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
